import axios from 'axios';
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  Save, Trash2, Search, CheckCircle, AlertCircle, CreditCard, 
  User, DollarSign, Loader2
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export const MappingComptesQB = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterMapped, setFilterMapped] = useState("all"); // "all", "mapped", "unmapped"

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [accountsRes, categoriesRes] = await Promise.all([
        axios.get(`${api.baseUrl}/quickbooks/accounts`),
        axios.get(`${api.baseUrl}/categories/list`)
      ]);

      const accountsData = accountsRes.data;
      const categoriesData = categoriesRes.data;

      setAccounts(accountsData.accounts || []);
      setCategories(categoriesData.categories || []);
    } catch (error) {
      console.error("Erreur chargement:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const saveMapping = async (account, categorie, accountType, needsMemo) => {
    setSaving(true);
    try {
      const response = await axios.post(`${api.baseUrl}/quickbooks/account-mapping`, {
          account: account,
          account_type: accountType,
          categorie: categorie || null,
          needs_memo_classification: needsMemo
        });

      if (response.status === 200) {
        toast.success(`✅ Mapping enregistré pour ${account}`);
        loadData();
      } else {
        toast.error("Erreur lors de l'enregistrement");
      }
    } catch (error) {
      console.error("Erreur save:", error);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const deleteMapping = async (account) => {
    try {
      const response = await axios.delete(`${api.baseUrl}/quickbooks/account-mapping/${encodeURIComponent(account)}`);

      if (response.status === 200) {
        toast.success(`Mapping supprimé pour ${account}`);
        loadData();
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch (error) {
      console.error("Erreur delete:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const getAccountIcon = (account) => {
    // Cartes de crédit
    if (account.includes("Visa") || account.includes("Master") || account.includes("CIBC")) {
      return <CreditCard className="w-4 h-4 text-blue-600" />;
    }
    // Comptes personnels (numéros 4xxx, 5xxx, 7xxx, 9xxx)
    if (/^[4579]\d{3}/.test(account)) {
      return <User className="w-4 h-4 text-purple-600" />;
    }
    // Autres
    return <DollarSign className="w-4 h-4 text-slate-600" />;
  };

  const isCreditCard = (account) => {
    return account.includes("Visa") || account.includes("Master") || account.includes("CIBC");
  };

  // Filtrer les comptes
  const filteredAccounts = accounts.filter(acc => {
    const matchesSearch = acc.account.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filterMapped === "mapped") {
      return matchesSearch && acc.mapping && acc.mapping.categorie;
    } else if (filterMapped === "unmapped") {
      return matchesSearch && (!acc.mapping || !acc.mapping.categorie);
    }
    
    return matchesSearch;
  });

  const stats = {
    total: accounts.length,
    mapped: accounts.filter(a => a.mapping && a.mapping.categorie).length,
    unmapped: accounts.filter(a => !a.mapping || !a.mapping.categorie).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="animate-fade-in">
        <h1 className="font-heading text-3xl font-bold text-slate-900">
          Mapping des Comptes QuickBooks
        </h1>
        <p className="text-slate-500 mt-1">
          Associez vos comptes QuickBooks à vos catégories de dépenses pour une classification automatique
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 animate-fade-in delay-100">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Comptes</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              </div>
              <DollarSign className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Mappés</p>
                <p className="text-2xl font-bold text-green-600">{stats.mapped}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Non mappés</p>
                <p className="text-2xl font-bold text-amber-600">{stats.unmapped}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtres */}
      <Card className="animate-fade-in delay-200">
        <CardContent className="pt-6">
          <div className="flex gap-4 items-center">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher un compte..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterMapped} onValueChange={setFilterMapped}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les comptes</SelectItem>
                <SelectItem value="mapped">Mappés uniquement</SelectItem>
                <SelectItem value="unmapped">Non mappés</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Liste des comptes */}
      <Card className="animate-fade-in delay-300">
        <CardHeader>
          <CardTitle>Comptes QuickBooks ({filteredAccounts.length})</CardTitle>
          <CardDescription>
            Associez chaque compte à une catégorie. Les cartes de crédit nécessiteront une classification par mémo.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {filteredAccounts.map((acc) => (
              <AccountRow
                key={acc.account}
                account={acc}
                categories={categories}
                onSave={saveMapping}
                onDelete={deleteMapping}
                getIcon={getAccountIcon}
                isCreditCard={isCreditCard}
                saving={saving}
              />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const AccountRow = ({ account, categories, onSave, onDelete, getIcon, isCreditCard, saving }) => {
  const [selectedCategory, setSelectedCategory] = useState(
    account.mapping?.categorie || "none"
  );
  const [accountType, setAccountType] = useState(
    account.mapping?.account_type || (isCreditCard(account.account) ? "credit_card" : "direct")
  );
  const [hasChanges, setHasChanges] = useState(false);

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    setHasChanges(true);
  };

  const handleSave = () => {
    const needsMemo = accountType === "credit_card";
    const catToSave = selectedCategory === "none" ? null : selectedCategory;
    onSave(account.account, catToSave, accountType, needsMemo);
    setHasChanges(false);
  };

  const isMapped = account.mapping && account.mapping.categorie;

  return (
    <div className="flex items-center gap-3 p-4 border rounded-lg hover:bg-slate-50 transition-colors">
      <div className="flex items-center gap-3 flex-1">
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-slate-100">
          {getIcon(account.account)}
        </div>
        
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <p className="font-medium text-slate-900">{account.account}</p>
            {isMapped && (
              <Badge variant="success" className="bg-green-100 text-green-700">
                <CheckCircle className="w-3 h-3 mr-1" />
                Mappé
              </Badge>
            )}
            {accountType === "credit_card" && (
              <Badge variant="outline" className="text-blue-600 border-blue-300">
                <CreditCard className="w-3 h-3 mr-1" />
                Carte
              </Badge>
            )}
          </div>
          <p className="text-sm text-slate-500">
            {account.transaction_count} transaction{account.transaction_count > 1 ? 's' : ''}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Select value={selectedCategory} onValueChange={handleCategoryChange}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder={accountType === "credit_card" ? "Classification par mémo" : "Choisir une catégorie"} />
          </SelectTrigger>
          <SelectContent>
            {accountType === "credit_card" && (
              <SelectItem value="none">Classification par mémo (manuel)</SelectItem>
            )}
            {categories.map((cat) => (
              <SelectItem key={cat.categorie} value={cat.categorie}>
                {cat.categorie}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {hasChanges && (
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Save className="w-4 h-4" />
          </Button>
        )}

        {isMapped && !hasChanges && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDelete(account.account)}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default MappingComptesQB;
